import React, { useEffect, useState } from "react";
import { Users, Eye, Map, Activity } from "lucide-react";
import { api } from "../lib/api";

/**
 * Site Activity — REAL traffic proof widget.
 *
 * Per product spec (2026-02-28): show the real, deduped visitor count even
 * when small. No rounded marketing tiers. If there are 2 visitors, show 2.
 * Designed to scale up as Google Ads spend ramps so attorneys, investors,
 * banks and merchants can see real activity live on the site.
 *
 * Numbers come from `/api/site/traffic-stats` (public, deduped by visitor cookie).
 */

const compact = (n) => {
    const x = Number(n || 0);
    if (x < 1000) return `${x}`;
    if (x < 10000) return `${(x / 1000).toFixed(1).replace(/\.0$/, "")}k`;
    if (x < 1000000) return `${Math.round(x / 1000)}k`;
    return `${(x / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
};

export default function SiteActivity({ className = "", testid = "site-activity" }) {
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.get("/site/traffic-stats")
            .then(({ data }) => { if (!cancelled) { setData(data); setLoaded(true); } })
            .catch(() => { if (!cancelled) { setData(null); setLoaded(true); } });
        return () => { cancelled = true; };
    }, []);

    if (!loaded || !data) return null;
    // Show even at small numbers — the user explicitly asked for real numbers.
    const total = data.visitors_all_time || 0;

    const cards = [
        {
            key: "today",
            icon: Activity,
            value: data.visitors_today,
            label: "today",
            sublabel: "live visitors",
        },
        {
            key: "week",
            icon: Users,
            value: data.visitors_7d,
            label: "this week",
            sublabel: "unique visitors",
        },
        {
            key: "all",
            icon: Eye,
            value: compact(total),
            label: "all-time",
            sublabel: `${compact(data.pageviews_all_time || 0)} page views`,
        },
    ];
    if ((data.states_count || 0) >= 2) {
        cards.push({
            key: "states",
            icon: Map,
            value: data.states_count,
            label: data.states_count === 1 ? "state" : "states",
            sublabel: "asking questions",
        });
    }

    return (
        <section
            className={`rounded-xl border border-border bg-card/50 px-5 py-4 sm:px-7 sm:py-5 ${className}`}
            data-testid={testid}
            aria-label="Site traffic activity"
        >
            <div className="flex items-center gap-2 mb-3">
                <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span className="text-[11px] uppercase tracking-wider font-semibold text-foreground/80">
                    Live site activity
                </span>
            </div>
            <div className="grid grid-cols-2 md:flex md:flex-wrap md:items-center md:justify-around gap-y-4 gap-x-6 text-center md:text-left">
                {cards.map((c) => {
                    const Icon = c.icon;
                    return (
                        <div key={c.key} className="flex items-center gap-3 justify-center md:justify-start" data-testid={`${testid}-${c.key}`}>
                            <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                <Icon className="h-4 w-4" />
                            </div>
                            <div className="text-left">
                                <div className="font-serif text-xl sm:text-2xl font-bold tracking-tight tabular-nums leading-none">
                                    {c.value}
                                </div>
                                <div className="text-[11px] sm:text-xs text-muted-foreground mt-1">
                                    <span className="font-medium text-foreground/80">{c.label}</span>{" "}
                                    <span className="opacity-70">{c.sublabel}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
